import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Plus, Trash2, Pencil, ChevronDown, ChevronRight, X,
  Wrench, Clock, Search, RefreshCw, Ban,
  ClipboardList, Bell, CheckCircle2, RotateCcw,
  AlertTriangle, ArrowRight, Package, UserCog, Cog,
  Check, Maximize2
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { pageCache } from '../utils/pageCache';

const CACHE_PLANS = 'maintenance_plans';

// ─── Config statuts ───────────────────────────────────────────────────────────

const STATUT_CFG = {
  en_attente:           { label: 'En attente',            bg: '#EFF6FF', color: '#1D4ED8' },
  a_faire:              { label: 'À faire',               bg: '#F5F3FF', color: '#7C3AED' },
  aucun_compteur_saisi: { label: 'Aucun compteur saisi',  bg: '#FEF2F2', color: '#B91C1C' },
  a_prospecter:         { label: 'À prospecter',          bg: '#F0F9FF', color: '#0369A1' },
  notifie_par_mail:     { label: 'Notifié par mail',      bg: '#ECFDF5', color: '#065F46' },
  reporte:              { label: 'Reporté',               bg: '#F1F5F9', color: '#475569' },
  converti_en_or:       { label: 'Converti en OR',        bg: '#F0FDF4', color: '#15803D' },
  annule:               { label: 'Annulé',                bg: '#F9FAFB', color: '#9CA3AF' },
};

const TYPE_LABEL = { h: 'Heures', km: 'Kilomètres', les_deux: 'Heures + Km', cycles: 'Cycles' };
const TYPE_UNIT  = { h: 'h', km: 'km', les_deux: 'h', cycles: 'cyc' };

// Filtres cockpit
const FILTRES = [
  { key: 'actif',       label: 'Alertes actives' },
  { key: 'en_retard',   label: 'En retard' },
  { key: 'tous',        label: 'Tout afficher' },
  { key: 'historique',  label: 'Historique (convertis)' },
  { key: 'prospection', label: 'Prospection' },
];

// Actions par statut
const ACTIONS_CFG = {
  a_faire: [
    { key: 'or',       label: 'Créer un OR',    action: 'or',                                     style: 'success' },
    { key: 'reporter', label: 'Reporter',        action: 'transition', statut: 'reporte',          style: 'muted' },
    { key: 'annuler',  label: 'Annuler',         action: 'transition', statut: 'annule',           style: 'danger' },
  ],
  notifie_par_mail: [
    { key: 'or',       label: 'Créer un OR',    action: 'or',                                     style: 'success' },
    { key: 'reporter', label: 'Reporter',        action: 'transition', statut: 'reporte',          style: 'muted' },
    { key: 'annuler',  label: 'Annuler',         action: 'transition', statut: 'annule',           style: 'danger' },
  ],
  aucun_compteur_saisi: [
    { key: 'mail',     label: 'Relancer par mail', action: 'mail',                                style: 'warning' },
    { key: 'or',       label: 'Créer un OR',       action: 'or',                                  style: 'success' },
    { key: 'annuler',  label: 'Annuler',           action: 'transition', statut: 'annule',         style: 'danger' },
  ],
  reporte: [
    { key: 'reactiver', label: 'Réactiver',      action: 'transition', statut: 'a_faire',          style: 'primary' },
    { key: 'annuler',   label: 'Annuler',        action: 'transition', statut: 'annule',           style: 'danger' },
  ],
  a_prospecter: [
    { key: 'mail',     label: 'Relancer par mail', action: 'mail',                                style: 'secondary' },
    { key: 'annuler',  label: 'Annuler',           action: 'transition', statut: 'annule',         style: 'danger' },
  ],
  en_attente: [
    { key: 'email_attente', label: 'Envoyer un email', action: 'email_attente', statut: 'notifie_par_mail', style: 'secondary', clientOnly: true },
    { key: 'or_attente',    label: 'Créer un OR',      action: 'or',                                        style: 'success' },
    { key: 'ignorer',       label: 'Ignorer cette étape', action: 'transition', statut: 'annule',           style: 'danger' },
  ],
};

// ─── Helpers visuels ──────────────────────────────────────────────────────────

function StatutBadge({ statut }) {
  const cfg = STATUT_CFG[statut] || { label: statut, bg: '#F3F4F6', color: '#374151' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function EcheanceBadge({ rec }) {
  const { jours_restants, is_en_retard, compteur_actuel, compteur_cible, preavis_jours, statut } = rec;

  if (statut === 'aucun_compteur_saisi' || statut === 'converti_en_or' || statut === 'annule') {
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  }

  // En retard (compteur > cible)
  if (is_en_retard && compteur_actuel && compteur_cible) {
    const delta = Math.abs(parseFloat(compteur_actuel) - parseFloat(compteur_cible));
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
        style={{ background: '#FEE2E2', color: '#DC2626' }}>
        <AlertTriangle size={10} />+{delta.toLocaleString('fr-FR')} h
      </span>
    );
  }

  if (jours_restants == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;

  const n      = Math.round(jours_restants);
  const pravis = parseInt(preavis_jours || 30);
  // Vert = hors préavis, Orange = dans le préavis
  const [bg, color] = n > pravis
    ? ['#F0FDF4', '#15803D']   // vert — en_attente
    : ['#FFF7ED', '#C2410C'];  // orange — dans le préavis

  return (
    <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full"
      style={{ background: bg, color }}>
      J-{n}
    </span>
  );
}

function StatCard({ label, count, color, active, onClick }) {
  const colors = {
    amber:  { bg: active ? '#F59E0B' : '#FEF9C3', text: active ? 'white' : '#A16207', border: '#FDE68A' },
    blue:   { bg: active ? '#3B82F6' : '#EFF6FF', text: active ? 'white' : '#1D4ED8', border: '#BFDBFE' },
    red:    { bg: active ? '#EF4444' : '#FEF2F2', text: active ? 'white' : '#B91C1C', border: '#FECACA' },
    slate:  { bg: active ? '#475569' : '#F1F5F9', text: active ? 'white' : '#475569', border: '#CBD5E1' },
    crimson:{ bg: active ? '#991B1B' : '#FFF1F2', text: active ? 'white' : '#991B1B', border: '#FECACA' },
  };
  const c = colors[color] || colors.slate;
  return (
    <button onClick={onClick}
      className="flex flex-col items-center px-4 py-2.5 rounded-xl border font-semibold text-sm transition-all cursor-pointer flex-shrink-0"
      style={{ background: c.bg, color: c.text, borderColor: c.border, minWidth: 110 }}>
      <span className="text-2xl font-bold leading-tight">{count ?? 0}</span>
      <span className="text-xs mt-0.5" style={{ opacity: active ? 1 : 0.8 }}>{label}</span>
    </button>
  );
}

// ─── Combobox article ─────────────────────────────────────────────────────────

function ArticleCombobox({ value, articles, onChange, placeholder, className, style }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);
  const inputRef            = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = articles.find(a => a.article_id === value);
  const filtered = articles.filter(a => {
    const q = search.toLowerCase();
    return !q || (a.designation || '').toLowerCase().includes(q) || (a.reference || '').toLowerCase().includes(q);
  }).slice(0, 50);
  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <div onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 30); }}
        className={`flex items-center justify-between gap-2 cursor-pointer ${className || ''}`}
        style={{ minHeight: 32, ...style }}>
        {selected
          ? <span className="truncate flex-1 text-sm" style={{ color: 'var(--text)' }}>
              {selected.reference && <span className="text-xs mr-1.5 font-mono" style={{ color: 'var(--text-muted)' }}>{selected.reference}</span>}
              {selected.designation || selected.nom}
            </span>
          : <span className="flex-1 text-sm italic" style={{ color: 'var(--text-placeholder)' }}>{placeholder || '— Choisir —'}</span>
        }
        <ChevronDown size={13} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-0.5 bg-white border rounded-xl shadow-xl z-50 overflow-hidden" style={{ borderColor: 'var(--border)', minWidth: 260 }}>
          <div className="p-2 border-b" style={{ borderColor: 'var(--border-light)' }}>
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher référence ou désignation…"
              className="w-full text-xs outline-none" style={{ color: 'var(--text)' }} />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0
              ? <p className="p-3 text-xs text-center" style={{ color: 'var(--text-placeholder)' }}>Aucun résultat</p>
              : filtered.map(a => (
                <button key={a.article_id} onMouseDown={() => { onChange(a.article_id); setOpen(false); setSearch(''); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2"
                  style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <span className="w-24 flex-shrink-0 font-mono" style={{ color: 'var(--text-muted)' }}>{a.reference || '—'}</span>
                  <span className="truncate" style={{ color: 'var(--text)' }}>{a.designation || a.nom}</span>
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function ModeleCombobox({ value, modeles, onChange, className, style }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);
  const inputRef            = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = modeles.find(m => m.modele_id === value);
  const label    = selected ? [selected.marque_nom, selected.nom].filter(Boolean).join(' — ') : '— Tous les matériels —';
  const filtered = modeles.filter(m => {
    const q = search.toLowerCase();
    return !q || (m.nom || '').toLowerCase().includes(q) || (m.marque_nom || '').toLowerCase().includes(q);
  }).sort((a, b) => (a.marque_nom + a.nom).localeCompare(b.marque_nom + b.nom, 'fr'));
  return (
    <div ref={ref} className="relative">
      <div onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 30); }}
        className={`flex items-center justify-between gap-2 cursor-pointer ${className || ''}`}
        style={{ minHeight: 38, ...style }}>
        <span className="flex-1 truncate text-sm" style={{ color: selected ? 'var(--text)' : 'var(--text-placeholder)' }}>{label}</span>
        <ChevronDown size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-0.5 bg-white border rounded-xl shadow-xl z-50 overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="p-2 border-b" style={{ borderColor: 'var(--border-light)' }}>
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher marque ou modèle…"
              className="w-full text-xs outline-none" style={{ color: 'var(--text)' }} />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button onMouseDown={() => { onChange(''); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 italic"
              style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
              — Tous les matériels —
            </button>
            {filtered.map(m => (
              <button key={m.modele_id} onMouseDown={() => { onChange(m.modele_id); setOpen(false); setSearch(''); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                style={{ borderBottom: '1px solid var(--border-light)', color: 'var(--text)' }}>
                {m.marque_nom && <span style={{ color: 'var(--text-muted)' }}>{m.marque_nom} — </span>}{m.nom}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bloc lignes (pièces / MO / prestations) ─────────────────────────────────

const BLOC_CFG = {
  piece:       { label: 'Pièces détachées & consommables', icon: Package,  ph: 'Chercher une pièce dans le catalogue…',     unitLabel: 'Qté', articleType: 'piece_detachee' },
  mo:          { label: "Main-d'œuvre",                    icon: UserCog,  ph: 'Choisir un profil de main-d\'œuvre…',       unitLabel: 'h',   articleType: 'main_oeuvre' },
  prestation:  { label: 'Prestations & contrôles',         icon: Cog,      ph: 'Chercher une prestation externe / contrôle…', unitLabel: 'Qté', articleType: 'prestation' },
};

function BlocLignes({ type, lignes, articles, onChange }) {
  const cfg = BLOC_CFG[type];
  const Icon = cfg.icon;

  // Seuls les articles du type correspondant au bloc sont proposés
  const articlesFiltres = articles.filter(a => a.type === cfg.articleType);

  const handleSelect = (articleId) => {
    if (!articleId) return;
    // Évite le doublon si l'article est déjà dans la liste
    if (lignes.some(l => l.article_id === articleId)) { toast.error('Article déjà présent dans ce bloc'); return; }
    const art = articlesFiltres.find(a => a.article_id === articleId);
    onChange([...lignes, {
      article_id:  articleId,
      reference:   art?.reference || '',
      designation: art?.designation || art?.nom || '',
      quantite:    1,
      type_ligne:  type,
    }]);
  };

  const handleRemove = (i) => onChange(lignes.filter((_, j) => j !== i));
  const handleQte    = (i, v) => {
    const next = [...lignes];
    next[i] = { ...next[i], quantite: parseFloat(v) || 1 };
    onChange(next);
  };

  const inp = "border rounded-md text-xs px-2 py-1.5 focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 pb-1" style={{ borderBottom: '1px solid var(--border-light)' }}>
        <Icon size={14} style={{ color: 'var(--primary-solid)' }} />
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{cfg.label}</span>
      </div>

      {lignes.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: '#FAFAF8', color: 'var(--text-muted)' }}>
              <th className="text-left px-2 py-1.5 font-semibold">Article</th>
              <th className="text-right px-2 py-1.5 font-semibold w-16">{cfg.unitLabel}</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border-light)' }}>
                <td className="px-2 py-1.5">
                  <span className="font-mono" style={{ color: 'var(--text-muted)' }}>{l.reference || l.article_id}</span>
                  {l.designation && <span className="ml-2" style={{ color: 'var(--text)' }}>{l.designation}</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <input type="number" min="0.01" step="0.01" value={l.quantite} onChange={e => handleQte(i, e.target.value)}
                    className={`${inp} w-14 text-right`} style={inpSt} />
                </td>
                <td className="px-2 py-1">
                  <button onClick={() => handleRemove(i)} className="p-1 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><X size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Combobox : la sélection ajoute directement la ligne */}
      <div className="flex items-center gap-2 pt-1">
        <ArticleCombobox value="" articles={articlesFiltres} onChange={handleSelect} placeholder={cfg.ph}
          className={`${inp} flex-1`} style={inpSt} />
      </div>
    </div>
  );
}

// ─── Side panel Plan ──────────────────────────────────────────────────────────

const SCOPE_LABELS = {
  modele:  { label: 'Modèle générique',       badge: 'Générique', color: '#6B7280' },
  article: { label: 'Variante / Article',      badge: 'Variante',  color: '#7C3AED' },
  serie:   { label: 'Numéro de série (dédié)', badge: 'Dédié',     color: '#B45309' },
};

function SidePanelPlan({ open, onClose, plan, modeles, onSaved }) {
  const [nom, setNom]             = useState('');
  const [typeCompteur, setType]   = useState('h');
  const [scope, setScope]         = useState('modele');
  const [modeleId, setModeleId]   = useState('');
  const [articleId, setArticleId] = useState('');
  const [articleSearch, setArtSearch] = useState('');
  const [articleOpts, setArtOpts]     = useState([]);
  const [articleLabel, setArticleLabel] = useState(''); // désignation de l'article sélectionné
  const [materielId, setMaterielId]   = useState('');
  const [materielSearch, setMatSearch] = useState('');
  const [materielOpts, setMatOpts]    = useState([]);
  const [description, setDesc]    = useState('');
  const [preavisJours, setPreavis] = useState(30);
  const [actif, setActif]         = useState(true);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!open) return;
    if (plan) {
      setNom(plan.nom || ''); setType(plan.type_compteur || 'h');
      setDesc(plan.description || ''); setPreavis(plan.preavis_jours ?? 30);
      setActif(plan.actif !== false);
      if (plan.materiel_id) { setScope('serie');   setMaterielId(plan.materiel_id); setArticleId(''); setArticleLabel(''); setModeleId(''); }
      else if (plan.article_id) {
        setScope('article'); setArticleId(plan.article_id); setMaterielId(''); setModeleId('');
        // Charger la désignation de l'article existant
        api.getArticle(plan.article_id)
          .then(r => setArticleLabel(r.data?.reference || r.data?.designation || plan.article_id))
          .catch(() => setArticleLabel(plan.article_id));
      }
      else { setScope('modele'); setModeleId(plan.modele_id || ''); setArticleId(''); setArticleLabel(''); setMaterielId(''); }
    } else {
      setNom(''); setType('h'); setScope('modele'); setModeleId(''); setArticleId('');
      setArticleLabel(''); setMaterielId(''); setArtSearch(''); setMatSearch(''); setDesc(''); setPreavis(30); setActif(true);
    }
  }, [open, plan]);

  // Recherche articles type=materiel
  useEffect(() => {
    if (scope !== 'article' || articleSearch.length < 2) { setArtOpts([]); return; }
    const t = setTimeout(() => {
      api.getArticles({ q: articleSearch, type: 'materiel', slim: false, limit: 10, actif: true })
        .then(r => { const items = r.data?.items || r.data || []; setArtOpts(Array.isArray(items) ? items.slice(0, 10) : []); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [articleSearch, scope]);

  // Recherche matériels (N° de série)
  useEffect(() => {
    if (scope !== 'serie' || materielSearch.length < 2) { setMatOpts([]); return; }
    const t = setTimeout(() => {
      api.getMateriels({ q: materielSearch, limit: 10 })
        .then(r => setMatOpts((r.data?.items || r.data || []).slice(0, 10)))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [materielSearch, scope]);

  const handleSave = async () => {
    if (!nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (scope === 'modele' && !modeleId) { toast.error('Sélectionnez un modèle'); return; }
    if (scope === 'article' && !articleId) { toast.error('Sélectionnez un article'); return; }
    if (scope === 'serie' && !materielId) { toast.error('Sélectionnez un matériel'); return; }
    setSaving(true);
    try {
      const mod = modeles.find(m => m.modele_id === modeleId);
      const payload = {
        nom, type_compteur: typeCompteur,
        modele_id:   scope === 'modele'   ? (modeleId || null)   : null,
        modele_nom:  scope === 'modele'   ? (mod?.nom || null)    : null,
        article_id:  scope === 'article'  ? (articleId || null)  : null,
        materiel_id: scope === 'serie'    ? (materielId || null) : null,
        description: description || null, exploitant_ids: [],
        preavis_jours: Number(preavisJours) || 30, actif,
      };
      if (plan) { await api.updateGammeEntretien(plan.plan_id, payload); toast.success('Plan modifié'); }
      else        { await api.createGammeEntretien(payload); toast.success('Plan créé'); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const inp = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)', background: 'var(--bg)', color: 'var(--text)' };
  const TYPE_OPTS = [{ v: 'h', l: 'Heures' }, { v: 'km', l: 'Kilomètres' }, { v: 'cycles', l: 'Cycles' }, { v: 'les_deux', l: 'H + Km' }];
  const selMat = materielOpts.find(m => m.materiel_id === materielId);
  const selArt = articleOpts.find(a => a.article_id === articleId);

  return (
    <SidePanel open={open} onOpenChange={v => { if (!v) onClose(); }} title={plan ? 'Modifier le plan' : "Nouveau plan d'entretien"}
      footer={
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} data-testid="plan-save-btn"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: 'var(--primary-gradient)' }}>
            <Check size={15} /> {saving ? 'Enregistrement…' : (plan ? 'Mettre à jour' : 'Créer le plan')}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-semibold border" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>Annuler</button>
        </div>
      }>
      <div className="space-y-4">
        {/* Nom */}
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Nom du plan *</label>
          <input value={nom} onChange={e => setNom(e.target.value)} placeholder="ex: Plan Kubota Mini-pelle 5T" className={inp} style={inpSt} data-testid="plan-nom-input" />
        </div>

        {/* Portée — menu déroulant */}
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Portée du plan</label>
          <select value={scope} onChange={e => { setScope(e.target.value); setModeleId(''); setArticleId(''); setMaterielId(''); setArtSearch(''); setMatSearch(''); }}
            className={inp} style={inpSt}>
            <option value="modele">Modèle</option>
            <option value="article">Article</option>
            <option value="serie">Numéro de série</option>
          </select>
        </div>

        {/* Sélecteur selon la portée */}
        {scope === 'modele' && (
          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Modèle *</label>
            <ModeleCombobox value={modeleId} modeles={modeles} onChange={setModeleId} className={`${inp} flex`} style={inpSt} />
          </div>
        )}
        {scope === 'article' && (
          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Article (type matériel) *</label>
            <div className="relative">
              <input value={articleSearch || (articleId ? (selArt ? (selArt.reference || selArt.designation) : articleLabel) : '')}
                onChange={e => { setArtSearch(e.target.value); setArticleId(''); setArticleLabel(''); }}
                placeholder="Rechercher par référence ou désignation…"
                className={inp} style={inpSt} />
              {articleOpts.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border shadow-lg overflow-hidden"
                     style={{ background: 'var(--bg)', borderColor: 'var(--border)', maxHeight: 200, overflowY: 'auto' }}>
                  {articleOpts.map(a => (
                    <button key={a.article_id} type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                      style={{ color: 'var(--text)' }}
                      onClick={() => { setArticleId(a.article_id); setArticleLabel(a.reference || a.designation || a.article_id); setArtSearch(''); setArtOpts([]); }}>
                      {a.reference && <span className="font-mono text-xs mr-2" style={{ color: 'var(--text-muted)' }}>{a.reference}</span>}
                      <span className="font-medium">{a.designation}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {scope === 'serie' && (
          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Matériel (N° de série) *</label>
            <div className="relative">
              <input value={materielSearch || (selMat ? `${selMat.numero_serie || selMat.nom}` : '')}
                onChange={e => { setMatSearch(e.target.value); setMaterielId(''); }}
                placeholder="Rechercher par N° de série ou nom…"
                className={inp} style={inpSt} />
              {materielOpts.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border shadow-lg overflow-hidden"
                     style={{ background: 'var(--bg)', borderColor: 'var(--border)', maxHeight: 200, overflowY: 'auto' }}>
                  {materielOpts.map(m => (
                    <button key={m.materiel_id} type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                      style={{ color: 'var(--text)' }}
                      onClick={() => { setMaterielId(m.materiel_id); setMatSearch(''); setMatOpts([]); }}>
                      <span className="font-medium">{m.numero_serie || m.numero_parc}</span>
                      <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>{m.nom}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {materielId && !selMat && <p className="text-xs mt-1 font-medium" style={{ color: '#B45309' }}>Matériel sélectionné : {materielId}</p>}
          </div>
        )}

        {/* Type de compteur */}
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Type de compteur</label>
          <div className="grid grid-cols-4 gap-1.5">
            {TYPE_OPTS.map(o => (
              <button key={o.v} onClick={() => setType(o.v)} type="button"
                className="py-2 px-2 rounded-lg text-xs font-semibold border text-center transition-all"
                style={typeCompteur === o.v ? { background: 'var(--primary-gradient)', color: 'white', borderColor: 'transparent' } : { background: 'var(--bg)', color: 'var(--text-secondary)', borderColor: 'var(--border)' }}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Préavis */}
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Préavis (jours)</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" max="365" value={preavisJours}
              onChange={e => setPreavis(e.target.value)}
              className="w-24 border rounded-lg px-3 py-2 text-sm focus:outline-none" style={inpSt} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              jours avant la date prévue pour activer l'alerte
            </span>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Description</label>
          <textarea value={description} onChange={e => setDesc(e.target.value)} rows={3} placeholder="Suivi constructeur standard…"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none resize-none" style={inpSt} />
        </div>

        {/* Actif */}
        {plan && (
          <div className="flex items-center justify-between py-2 px-3 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Plan actif</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Désactiver stoppe la génération d'alertes</p>
            </div>
            <button onClick={() => setActif(a => !a)} type="button"
              className="w-11 h-6 rounded-full transition-colors relative"
              style={{ background: actif ? '#10B981' : '#D1D5DB' }}>
              <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                style={{ transform: actif ? 'translateX(20px)' : 'translateX(0)' }} />
            </button>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// ─── Side panel Étape ─────────────────────────────────────────────────────────

function SidePanelEtape({ open, onClose, plan, etape, articles, onSaved }) {
  const [nom,    setNom]    = useState('');
  const [heures, setHeures] = useState('');
  const [km,     setKm]     = useState('');
  const [travaux, setTrav]  = useState('');
  const [pieceL, setPL]     = useState([]);
  const [moL,    setML]     = useState([]);
  const [prestL, setPresL]  = useState([]);
  const [saving, setSaving] = useState(false);
  const [travOpen, setTravOpen] = useState(false);
  const travRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (etape) {
      setNom(etape.nom || ''); setHeures(etape.heures ? String(etape.heures) : '');
      setKm(etape.km ? String(etape.km) : ''); setTrav(etape.travaux || '');
      const lignes = etape.lignes || [];
      setPL(lignes.filter(l => (l.type_ligne || 'piece') === 'piece'));
      setML(lignes.filter(l => l.type_ligne === 'mo'));
      setPresL(lignes.filter(l => l.type_ligne === 'prestation'));
    } else {
      setNom(''); setHeures(''); setKm(''); setTrav('');
      setPL([]); setML([]); setPresL([]);
    }
  }, [open, etape]);

  useEffect(() => { if (travOpen) setTimeout(() => travRef.current?.focus(), 80); }, [travOpen]);

  const typeCompteur = plan?.type_compteur || 'h';
  const showH  = typeCompteur === 'h'  || typeCompteur === 'les_deux';
  const showKm = typeCompteur === 'km' || typeCompteur === 'les_deux';

  const handleSave = async () => {
    if (!nom.trim()) { toast.error('Le libellé est obligatoire'); return; }
    setSaving(true);
    try {
      const allLignes = [
        ...pieceL.map(l => ({ ...l, type_ligne: 'piece' })),
        ...moL.map(l => ({ ...l, type_ligne: 'mo' })),
        ...prestL.map(l => ({ ...l, type_ligne: 'prestation' })),
      ].filter(l => l.article_id);
      const payload = { nom, heures: parseInt(heures) || 0, km: parseInt(km) || 0, travaux: travaux || null, lignes: allLignes };
      if (etape) { await api.updateEtapeEntretien(etape.etape_id, payload); toast.success('Étape mise à jour'); }
      else if (plan) { await api.createEtapeEntretien(plan.plan_id, payload); toast.success('Étape ajoutée'); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const inp = "border rounded-lg px-3 py-2 text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const titre = etape
    ? `Étape : ${etape.nom || ''}`
    : `Nouvelle étape${plan ? ` — ${plan.nom}` : ''}`;

  return (
    <>
      <SidePanel open={open} onOpenChange={v => { if (!v) onClose(); }} title={titre} width={874}
        footer={
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving} data-testid="etape-save-btn"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--primary-gradient)' }}>
              <Check size={15} /> {saving ? 'Enregistrement…' : (etape ? 'Mettre à jour' : "Créer l'étape")}
            </button>
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-semibold border" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>Annuler</button>
          </div>
        }>
        <div className="space-y-4">
          {/* En-tête */}
          <div className="grid grid-cols-2 gap-3">
            {showH && (
              <div>
                <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Seuil horaire</label>
                <div className="relative">
                  <input type="number" min="0" value={heures} onChange={e => {
                    setHeures(e.target.value);
                    if (!nom.trim() || /^Entretien \d+ (h|km)$/.test(nom)) setNom(e.target.value ? `Entretien ${e.target.value} h` : '');
                  }} placeholder="500" className={`w-full ${inp} pr-7`} style={inpSt} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none" style={{ color: 'var(--text-placeholder)' }}>h</span>
                </div>
              </div>
            )}
            {showKm && (
              <div>
                <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Seuil km</label>
                <div className="relative">
                  <input type="number" min="0" value={km} onChange={e => {
                    setKm(e.target.value);
                    if (!nom.trim() || /^Entretien \d+ (h|km)$/.test(nom)) setNom(e.target.value ? `Entretien ${e.target.value} km` : '');
                  }} placeholder="10 000" className={`w-full ${inp} pr-10`} style={inpSt} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none" style={{ color: 'var(--text-placeholder)' }}>km</span>
                </div>
              </div>
            )}
            <div className={showH && showKm ? 'col-span-2' : ''}>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-muted)' }}>Libellé *</label>
              <input value={nom} onChange={e => setNom(e.target.value)} placeholder="ex: Entretien des 500 h" className={`w-full ${inp}`} style={inpSt} />
            </div>
          </div>

          {/* Travaux */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Travaux à effectuer</label>
              <button type="button" onClick={() => setTravOpen(true)}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded"
                style={{ color: 'var(--primary-solid)', background: 'var(--primary-50)' }}>
                <Maximize2 size={10} /> Plein écran
              </button>
            </div>
            <textarea value={travaux} onChange={e => setTrav(e.target.value)} rows={2} placeholder="Décrire les opérations…"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none resize-none" style={inpSt} />
          </div>

          {/* Séparateur */}
          <div className="border-t pt-3 space-y-5" style={{ borderColor: 'var(--border-light)' }}>
            <BlocLignes type="piece"      lignes={pieceL} articles={articles} onChange={setPL} />
            <BlocLignes type="mo"         lignes={moL}    articles={articles} onChange={setML} />
            <BlocLignes type="prestation" lignes={prestL} articles={articles} onChange={setPresL} />
          </div>
        </div>
      </SidePanel>

      {/* Modal travaux plein écran */}
      {travOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999, background: 'rgba(0,0,0,0.45)' }}
          onMouseDown={e => { if (e.target === e.currentTarget) setTravOpen(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col" style={{ width: 640, maxWidth: '92vw', maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="font-bold text-base" style={{ color: 'var(--text)' }}>Travaux — {nom || 'Étape'}</h3>
              <button onClick={() => setTravOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 flex-1 overflow-auto">
              <textarea ref={travRef} value={travaux} onChange={e => setTrav(e.target.value)} rows={14}
                className="w-full h-full border rounded-xl px-4 py-3 text-sm focus:outline-none"
                style={{ borderColor: 'var(--border)', resize: 'vertical', minHeight: 300 }} />
            </div>
            <div className="px-6 py-4 border-t flex justify-end flex-shrink-0" style={{ borderColor: 'var(--border-light)', background: '#FAFAF8' }}>
              <button onClick={() => setTravOpen(false)} className="px-5 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: 'var(--primary-gradient)' }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Onglet Plans d'entretien ─────────────────────────────────────────────────

function OngletPlans({ plans, articles, modeles, loading, onReload }) {
  const [expanded, setExpanded]   = useState({});
  const [panelPlan, setPanelPlan] = useState({ open: false, plan: null });
  const [panelEtape, setPanelEtape] = useState({ open: false, plan: null, etape: null });
  const [search, setSearch]       = useState('');

  const filtered = search.trim()
    ? plans.filter(p => { const q = search.toLowerCase(); return (p.nom || '').toLowerCase().includes(q) || (p.modele_nom || '').toLowerCase().includes(q); })
    : plans;

  const handleDeletePlan = async (pid) => {
    if (!window.confirm('Supprimer ce plan d\'entretien ?')) return;
    try { await api.deleteGammeEntretien(pid); toast.success('Plan supprimé'); onReload(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteEtape = async (eid, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette étape ?')) return;
    try { await api.deleteEtapeEntretien(eid); toast.success('Étape supprimée'); onReload(true); }
    catch (e2) { toast.error(e2?.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex justify-center h-40 items-center"><div className="animate-spin w-6 h-6 rounded-full border-2" style={{ borderColor: 'var(--primary-100)', borderTopColor: 'var(--primary-solid)' }} /></div>;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {plans.length > 0 && (
            <div className="relative flex-1 max-w-sm">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un plan…"
                className="w-full pl-8 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
                style={{ borderColor: 'var(--border)' }} />
            </div>
          )}
        </div>
        <button onClick={() => setPanelPlan({ open: true, plan: null })} data-testid="plan-add-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white flex-shrink-0"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={15} /> Nouveau plan d'entretien
        </button>
      </div>

      {plans.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: 'var(--border)' }}>
          <Wrench size={40} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>Aucun plan d'entretien</p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Créez un plan pour définir les opérations périodiques de maintenance</p>
          <button onClick={() => setPanelPlan({ open: true, plan: null })} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={15} /> Créer un plan
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* En-tête table */}
          <div className="grid px-5 py-2 text-xs font-semibold uppercase tracking-wide"
            style={{ gridTemplateColumns: '1fr auto auto auto', gap: 16, color: 'var(--text-muted)', background: '#FAFAF8', borderRadius: 8, border: '1px solid var(--border-light)' }}>
            <span>Plan & modèle associé</span><span>Compteur</span><span>Étapes</span><span>Actions</span>
          </div>
          {filtered.map(plan => {
            const isExp = expanded[plan.plan_id];
            return (
              <div key={plan.plan_id} className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                {/* Header plan */}
                <div className="grid items-center px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{ gridTemplateColumns: '1fr auto auto auto', gap: 16 }}
                  onClick={() => setExpanded(e => ({ ...e, [plan.plan_id]: !e[plan.plan_id] }))}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{isExp ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
                    <div className="min-w-0">
                      <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{plan.nom}</span>
                      {plan.materiel_id ? (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{ background: '#FFF7ED', color: '#B45309' }}>
                          N° série : {plan.materiel_serie || plan.materiel_parc || plan.materiel_nom || plan.materiel_id}
                        </span>
                      ) : plan.article_id ? (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{ background: '#F5F3FF', color: '#7C3AED' }}>
                          Article : {plan.article_reference ? `${plan.article_reference} — ` : ''}{plan.article_designation || plan.article_id}
                        </span>
                      ) : plan.modele_nom ? (
                        <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                          Modèle : {plan.modele_nom}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                    {TYPE_LABEL[plan.type_compteur] || 'Heures'}
                  </span>
                  <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                    {plan.etapes?.length || 0} étape{(plan.etapes?.length || 0) !== 1 ? 's' : ''}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setPanelPlan({ open: true, plan })} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border hover:bg-gray-50" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                      <Pencil size={12} /> Éditer plan
                    </button>
                    <button onClick={() => handleDeletePlan(plan.plan_id)} className="p-1.5 rounded-md hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={13} /></button>
                  </div>
                </div>

                {/* Étapes (dépliées) */}
                {isExp && (
                  <div className="border-t" style={{ borderColor: 'var(--border-light)' }}>
                    {plan.etapes?.length > 0 ? (
                      <>
                        <div className="grid px-6 py-2 text-xs font-semibold uppercase tracking-wide"
                          style={{ gridTemplateColumns: '1.5fr 1fr 1fr auto', gap: 12, background: '#FAFAF8', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                          <span>Opération</span><span>Périodicité</span><span>Éléments</span><span></span>
                        </div>
                        {[...plan.etapes].sort((a, b) => (a.heures || 0) - (b.heures || 0)).map((etape, idx) => {
                          const nbLignes = (etape.lignes || []).length;
                          return (
                            <div key={etape.etape_id}
                              className="grid px-6 py-3 items-center cursor-pointer hover:bg-gray-50 transition-colors"
                              style={{ gridTemplateColumns: '1.5fr 1fr 1fr auto', gap: 12, borderBottom: idx < plan.etapes.length - 1 ? '1px solid var(--border-light)' : 'none' }}
                              onClick={() => setPanelEtape({ open: true, plan, etape })}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>{idx + 1}</span>
                                <span className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>↳ {etape.nom}</span>
                              </div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {etape.heures > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#FEF3C7', color: '#B45309' }}>
                                    <Clock size={9} /> {etape.heures.toLocaleString('fr-FR')} h
                                  </span>
                                )}
                                {etape.km > 0 && (
                                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                                    {etape.km.toLocaleString('fr-FR')} km
                                  </span>
                                )}
                              </div>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {nbLignes} élément{nbLignes !== 1 ? 's' : ''}
                              </span>
                              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <button onClick={() => setPanelEtape({ open: true, plan, etape })} className="p-1.5 rounded-md hover:bg-gray-100" style={{ color: 'var(--text-muted)' }} title="Modifier"><Pencil size={13} /></button>
                                <button onClick={e => handleDeleteEtape(etape.etape_id, e)} className="p-1.5 rounded-md hover:bg-red-50" style={{ color: '#DC2626' }} title="Supprimer"><Trash2 size={13} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <p className="px-6 py-4 text-sm italic" style={{ color: 'var(--text-placeholder)' }}>Aucune étape définie pour ce plan</p>
                    )}
                    <div className="px-6 py-3 border-t" style={{ borderColor: 'var(--border-light)', background: '#FAFAF8' }}>
                      <button onClick={() => setPanelEtape({ open: true, plan, etape: null })} data-testid={`add-etape-${plan.plan_id}`}
                        className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border"
                        style={{ borderColor: 'var(--primary-100)', color: 'var(--primary-solid)', background: 'var(--primary-50)' }}>
                        <Plus size={12} /> Ajouter une étape d'entretien
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SidePanelPlan open={panelPlan.open} onClose={() => setPanelPlan({ open: false, plan: null })}
        plan={panelPlan.plan} modeles={modeles}
        onSaved={() => { setPanelPlan({ open: false, plan: null }); onReload(true); }} />

      <SidePanelEtape open={panelEtape.open} onClose={() => setPanelEtape({ open: false, plan: null, etape: null })}
        plan={panelEtape.plan} etape={panelEtape.etape} articles={articles}
        onSaved={() => { setPanelEtape({ open: false, plan: null, etape: null }); onReload(true); }} />
    </div>
  );
}

// ─── Onglet Cockpit Préventif ─────────────────────────────────────────────────

function OngletCockpit() {
  const [records, setRecords]         = useState([]);
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(true);
  const [filtre, setFiltre]           = useState('actif');
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDS]      = useState('');
  const [acting, setActing]           = useState(null);
  const [syncing, setSyncing]         = useState(false);
  const [reportModal, setReportModal] = useState(null); // { rec }

  useEffect(() => { const t = setTimeout(() => setDS(search), 300); return () => clearTimeout(t); }, [search]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        api.getPreventifs({ filtre, search: debouncedSearch || undefined }),
        api.getPreventifStats(),
      ]);
      setRecords(Array.isArray(rRes.data) ? rRes.data : []);
      setStats(sRes.data || {});
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [filtre, debouncedSearch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleAction = async (rec, action, statut) => {
    // Reporter → ouvre la modale de date
    if (action === 'transition' && statut === 'reporte') {
      setReportModal({ rec });
      return;
    }
    const key = rec.preventif_id + '_' + action;
    setActing(key);
    try {
      if (action === 'or') {
        if (!window.confirm('Créer un Ordre de Réparation depuis cet enregistrement préventif ?')) { setActing(null); return; }
        const res = await api.convertirPreventifEnOR(rec.preventif_id);
        toast.success(`OR créé : ${res.data.numero}`);
      } else if (action === 'mail' || action === 'email_attente') {
        if (action === 'email_attente') {
          const res = await api.envoyerEmailPreventif(rec.preventif_id);
          if (res.data?.sans_responsable) {
            toast.warning('Aucun Responsable technique trouvé pour ce client (vérifiez les tags dans l\'annuaire)');
          } else {
            toast.success(`Email envoyé à ${res.data?.nb_destinataires || 1} destinataire(s)`);
          }
        } else {
          // a_prospecter — relance commerciale avec cooldown
          const res = await api.relancerMailPreventif(rec.preventif_id);
          if (res.data?.en_cooldown) {
            toast.info(`Déjà contacté récemment — prochain envoi possible le ${res.data.prochain_envoi}`);
          } else if (res.data?.sans_responsable) {
            toast.warning('Aucun Responsable technique trouvé pour ce client');
          } else {
            toast.success(`Email prospection envoyé à ${res.data?.nb_destinataires || 1} destinataire(s)`);
          }
        }
      } else {
        if (statut === 'annule' && !window.confirm('Ignorer cette étape ? L\'alerte sera marquée comme annulée et le batch calculera le prochain seuil.')) { setActing(null); return; }
        await api.transitionPreventif(rec.preventif_id, { statut });
        toast.success('Statut mis à jour');
      }
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setActing(null); }
  };

  const handleReporter = async (rec, dateReporte) => {
    setReportModal(null);
    const key = rec.preventif_id + '_reporter';
    setActing(key);
    try {
      await api.transitionPreventif(rec.preventif_id, { statut: 'reporte', date_reporte: dateReporte });
      toast.success(`Reporté au ${new Date(dateReporte).toLocaleDateString('fr-FR')}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setActing(null); }
  };

  const STYLE_BTN = {
    primary:   { bg: 'var(--primary-solid)', color: '#FFFFFF', border: 'var(--primary-solid)' },
    success:   { bg: '#059669',              color: '#FFFFFF', border: '#059669' },
    secondary: { bg: '#0284C7',              color: '#FFFFFF', border: '#0284C7' },
    muted:     { bg: '#64748B',              color: '#FFFFFF', border: '#64748B' },
    warning:   { bg: '#D97706',              color: '#FFFFFF', border: '#D97706' },
    danger:    { bg: '#DC2626',              color: '#FFFFFF', border: '#DC2626' },
  };

  const currentFiltreCfg = FILTRES.find(f => f.key === filtre) || FILTRES[0];

  return (
    <div className="space-y-4">
      {/* Cartes stats */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <StatCard label="À faire"        count={stats.a_faire}            color="amber"   active={filtre === 'actif'}      onClick={() => setFiltre('actif')} />
        <StatCard label="Notifiés"       count={stats.notifie_par_mail}   color="blue"    active={false}                   onClick={() => setFiltre('actif')} />
        <StatCard label="En attente"     count={stats.en_attente}         color="indigo"  active={filtre === 'actif'}      onClick={() => setFiltre('actif')} />
        <StatCard label="Sans compteur"  count={stats.aucun_compteur_saisi} color="red"   active={false}                   onClick={() => setFiltre('actif')} />
        <StatCard label="En retard"     count={stats.en_retard}        color="crimson" active={filtre === 'en_retard'}  onClick={() => setFiltre('en_retard')} />
      </div>

      {/* Barre search + filtre */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="relative flex-1" style={{ minWidth: 240 }}>
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="cockpit-search"
            placeholder="Rechercher matériel, N° série, client…"
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)' }} />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={13} /></button>}
        </div>

        {/* Dropdown filtre */}
        <select value={filtre} onChange={e => setFiltre(e.target.value)}
          className="border rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          {FILTRES.map(f => (
            <option key={f.key} value={f.key}>{f.label}{f.key === 'actif' && stats.a_faire != null ? ` (${(stats.a_faire || 0) + (stats.notifie_par_mail || 0) + (stats.aucun_compteur_saisi || 0)})` : ''}</option>
          ))}
        </select>

        <button onClick={async () => { setSyncing(true); try { await api.runBatchPreventif(); } catch {} await loadAll(); setSyncing(false); }}
          disabled={syncing} data-testid="sync-preventif-btn"
          className="p-2.5 rounded-xl border hover:bg-gray-50 disabled:opacity-60" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }} title="Actualiser">
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center h-40 items-center"><div className="animate-spin w-6 h-6 rounded-full border-2" style={{ borderColor: 'var(--primary-100)', borderTopColor: 'var(--primary-solid)' }} /></div>
      ) : records.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: 'var(--border)' }}>
          <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>Aucune alerte préventive</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{currentFiltreCfg.label} — Les alertes apparaîtront automatiquement pour les matériels avec suivi actif.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '14%' }} />  {/* Matériel */}
                <col style={{ width: '12%' }} />  {/* Locataire / Propriétaire */}
                <col style={{ width: '15%' }} />  {/* Plan & Échéance */}
                <col style={{ width: '9%' }} />   {/* Compteur */}
                <col style={{ width: '9%' }} />   {/* Cible */}
                <col style={{ width: '9%' }} />   {/* Échéance */}
                <col style={{ width: '9%' }} />   {/* Date prévue */}
                <col style={{ width: '9%' }} />   {/* Statut */}
                <col style={{ width: '14%' }} />  {/* Actions */}
              </colgroup>
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide"
                  style={{ background: '#FAFAF8', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                  <th className="text-left px-4 py-3">Matériel</th>
                  <th className="text-left px-4 py-3">Locataire / Propriétaire</th>
                  <th className="text-left px-4 py-3">Plan & Échéance</th>
                  <th className="text-center px-4 py-3 align-middle">Compteur</th>
                  <th className="text-center px-4 py-3 align-middle">Cible</th>
                  <th className="text-center px-4 py-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Échéance</th>
                  <th className="text-center px-4 py-3 align-middle">Date prévue</th>
                  <th className="text-center px-4 py-3 align-middle">Statut</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => {
                  const actions = (ACTIONS_CFG[rec.statut] || []).filter(act =>
                    !act.clientOnly || rec.mat_parc === 'client'
                  );
                  return (
                    <tr key={rec.preventif_id} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: 'var(--border-light)' }}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm" style={{ color: 'var(--text)' }}>{rec.mat_nom || '—'}</div>
                        {rec.numero_serie && <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>SN : {rec.numero_serie}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs" style={{ color: 'var(--text)' }}>{rec.exploitant_nom || '—'}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{rec.proprietaire_nom || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>{rec.gamme_nom || '—'}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{rec.etape_nom || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-center align-middle font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {rec.compteur_actuel != null ? `${Number(rec.compteur_actuel).toLocaleString('fr-FR')} h` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center align-middle">
                        {rec.compteur_cible != null
                          ? <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#B45309', whiteSpace: 'nowrap' }}>{Number(rec.compteur_cible).toLocaleString('fr-FR')} h</span>
                          : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td className="px-4 py-3 text-center align-middle">
                        {rec.statut === 'aucun_compteur_saisi' ? (
                          <span className="text-xs" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {rec.date_relance_email
                              ? `Relancé le ${new Date(rec.date_relance_email).toLocaleDateString('fr-FR')}`
                              : '—'}
                          </span>
                        ) : rec.statut === 'reporte' && rec.date_reporte ? (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#F1F5F9', color: '#475569', whiteSpace: 'nowrap' }}>
                            Reporté au {new Date(rec.date_reporte).toLocaleDateString('fr-FR')}
                          </span>
                        ) : (
                          <EcheanceBadge rec={rec} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center align-middle text-xs" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {rec.date_prevue_seuil
                          ? new Date(rec.date_prevue_seuil).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-center align-middle"><StatutBadge statut={rec.statut} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {actions.map(act => {
                            const key = rec.preventif_id + '_' + act.key;
                            const isLoading = acting === key || (act.key === 'reporter' && acting === rec.preventif_id + '_reporter');
                            const s = STYLE_BTN[act.style] || STYLE_BTN.muted;
                            return (
                              <button key={act.key} disabled={!!acting} onClick={() => handleAction(rec, act.action, act.statut)}
                                data-testid={`action-${act.key}-${rec.preventif_id}`}
                                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold border transition-all disabled:opacity-50"
                                style={{ background: s.bg, color: s.color, borderColor: s.border }}>
                                {isLoading
                                  ? <div className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin" />
                                  : null
                                }
                                {act.label}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-xs text-right" style={{ color: 'var(--text-disabled)', borderTop: '1px solid var(--border-light)' }}>
            {records.length} enregistrement{records.length > 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Modale Reporter */}
      {reportModal && (
        <ReportModal
          rec={reportModal.rec}
          onConfirm={(date) => handleReporter(reportModal.rec, date)}
          onClose={() => setReportModal(null)}
        />
      )}
    </div>
  );
}

function ReportModal({ rec, onConfirm, onClose }) {
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const [date, setDate] = useState(minDate.toISOString().slice(0, 10));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-base" style={{ color: 'var(--text)' }}>Reporter l'entretien</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          <span className="font-medium">{rec.mat_nom}</span> — {rec.etape_nom || rec.gamme_nom}
        </p>
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
            Nouvelle date cible
          </label>
          <input type="date" value={date} min={minDate.toISOString().slice(0, 10)}
            onChange={e => setDate(e.target.value)}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)' }} />
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Le batch réactivera automatiquement cet entretien quand la date sera atteinte.
        </p>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
            Annuler
          </button>
          <button onClick={() => date && onConfirm(date)} disabled={!date}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'var(--primary-solid)' }}>
            Confirmer le report
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function Maintenance() {
  const [tab, setTab]         = useState('cockpit');
  const [plans, setPlans]     = useState([]);
  const [articles, setArticles] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = async (force = false) => {
    if (!force) {
      const cached = pageCache.get(CACHE_PLANS);
      if (cached) { setPlans(cached.plans); setArticles(cached.articles); setModeles(cached.modeles); setLoading(false); return; }
    }
    try {
      const [g, a, m] = await Promise.all([
        api.getGammesEntretien(),
        api.getArticles({ slim: true }),
        api.getModeles(),
      ]);
      const data = { plans: g.data || [], articles: Array.isArray(a.data) ? a.data : [], modeles: Array.isArray(m.data) ? m.data : [] };
      setPlans(data.plans); setArticles(data.articles); setModeles(data.modeles);
      pageCache.set(CACHE_PLANS, data);
    } catch { toast.error('Erreur chargement plans'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPlans(); }, []); // eslint-disable-line

  const TABS = [
    { key: 'cockpit', label: 'Cockpit Préventif', icon: ClipboardList },
    { key: 'plans',   label: "Plans d'entretien", icon: Wrench },
  ];

  return (
    <div className="space-y-4" data-testid="maintenance-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)' }}>Maintenance Préventive</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Plans d'entretien et suivi des seuils par compteur</p>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-0 border-b" style={{ borderColor: 'var(--border-light)' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px"
              style={isActive
                ? { borderBottomColor: 'var(--primary-solid)', color: 'var(--primary-solid)' }
                : { borderBottomColor: 'transparent', color: 'var(--text-muted)' }}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Contenu */}
      <div>
        {tab === 'cockpit' && <OngletCockpit />}
        {tab === 'plans'   && <OngletPlans plans={plans} articles={articles} modeles={modeles} loading={loading} onReload={loadPlans} />}
      </div>
    </div>
  );
}
